/**
 * PressBoxSetupPage — the standalone Press Box onboarding. 2026-10-18,
 * reworked 2026-10-19 (coach feedback): numbered steps, real Continue/Back
 * buttons, recorder roles on the invites.
 *
 * Three steps:
 *   1. The team — name + logo (the logo shows on the public stats site).
 *   2. The schedule — webcal link, or a PDF/Excel the stats AI reads;
 *      games can also be added one by one later.
 *   3. Who records — you (the manager), plus invited parents with a role:
 *      record only, record + edit stats, or full admin.
 *
 * The team is created when step 1 continues; steps 2–3 work on it directly,
 * so closing the browser halfway loses nothing. Back from step 2 reopens
 * step 1 to fix the name or logo (the team is updated, not re-created).
 */
import { useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Radio, Upload, Loader2, ArrowRight, ArrowLeft, Mail, CheckCircle2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';
import { PRESS_BOX } from '../../constants/pressBox.js';
import { useAuth } from '../../context/AuthContext.jsx';
import ScheduleImportCard from './ScheduleImportCard.jsx';

const fileB64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || ''));
  r.onerror = () => reject(new Error('Could not read the file'));
  r.readAsDataURL(file);
});

const STEP_TITLES = { 1: 'Your team', 2: "The season's games", 3: 'Who records the games' };

function StepHeader({ step }) {
  return (
    <div className="mb-4">
      <div className="text-xs font-bold" style={{ color: 'var(--gold)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        Step {step} of 3 — {STEP_TITLES[step]}
      </div>
      <div className="flex items-center gap-2 mt-2" aria-hidden="true">
        {[1, 2, 3].map(n => (
          <span key={n} style={{ width: n === step ? 26 : 10, height: 8, borderRadius: 4, transition: 'width .2s',
            background: n <= step ? 'var(--gold)' : 'var(--border)' }} />
        ))}
      </div>
    </div>
  );
}

/** The sticky Continue / Back row every step ends with. */
function StepNav({ onBack, onNext, nextLabel = 'Continue', nextIcon = <ArrowRight size={14} />, busy = false, secondary = null }) {
  return (
    <div className="flex items-center gap-2 mt-4 mb-6" style={{ flexWrap: 'wrap' }}>
      {onBack && (
        <button type="button" className="btn-ghost" onClick={onBack} disabled={busy}>
          <ArrowLeft size={14} /> Back
        </button>
      )}
      <button type="button" className="btn-ice" onClick={onNext} disabled={busy}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : nextIcon} {nextLabel}
      </button>
      {secondary}
    </div>
  );
}

const ROLE_OPTIONS = [
  ['record', 'Record games only', 'They can record at the rink and upload tapes — nothing else.'],
  ['edit', 'Record + edit stats', 'Recording, plus fixing goals and assists after the game.'],
  ['admin', 'Full admin', 'Everything: record, edit stats, manage the roster, schedule and settings.'],
];

export default function PressBoxSetupPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loadTeams } = useOutletContext() || {};
  const [step, setStep] = useState(1);
  const [team, setTeam] = useState(null);

  // step 1
  const [name, setName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const logoRef = useRef(null);

  // step 2
  const [gamesAdded, setGamesAdded] = useState(0);

  // step 3
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('record');
  const [inviting, setInviting] = useState(false);
  const [invited, setInvited] = useState([]);

  const pickLogo = (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return toast.error('The logo must be a PNG, JPEG or WebP image');
    if (file.size > 2 * 1024 * 1024) return toast.error('The logo must be 2 MB or smaller');
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  // Step 1 Continue: create the team the first time; after Back, update it.
  const saveTeamAndContinue = async () => {
    const n = name.trim();
    if (!n) return toast.error('Give the team a name');
    setCreating(true);
    try {
      let t = team;
      if (!t) {
        const year = new Date().getFullYear();
        const { data } = await api.post('/teams', {
          name: n, teamType: 'press_box',
          initialSeasonName: `${year}–${String(year + 1).slice(2)}`,
        });
        t = data.team || data;
        setTeam(t);
      } else if (t.name !== n) {
        await api.put(`/teams/${t.id}`, { name: n }).catch(() => toast.error('The name change didn’t save — fix it later in Settings'));
        setTeam({ ...t, name: n });
      }
      if (logoFile) {
        try {
          await api.post(`/teams/${t.id}/press-box/logo`, { data: await fileB64(logoFile), mimeType: logoFile.type });
          setLogoFile(null);   // saved; don't re-upload on another pass
        } catch (e) { toast.error(e.response?.data?.error || 'The team was made, but the logo didn’t save — add it later in Settings'); }
      }
      loadTeams?.(t.id);   // the layout re-reads teams, the slim Press Box menu appears
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not create the team');
    } finally { setCreating(false); }
  };

  const invite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error('That doesn’t look like an email address');
    setInviting(true);
    try {
      await api.post(`/teams/${team.id}/invite`, {
        email, name: inviteName.trim() || email.split('@')[0],
        memberRole: inviteRole === 'admin' ? 'coach' : 'manager',
        pressBoxRole: inviteRole,
      });
      setInvited((l) => [...l, { email, role: inviteRole }]);
      setInviteEmail(''); setInviteName('');
      toast.success('Invitation sent');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not send the invite');
    } finally { setInviting(false); }
  };

  const finish = () => navigate(`/press-box/${team.id}`);
  const roleLabel = (v) => ROLE_OPTIONS.find(([k]) => k === v)?.[1] || v;

  return (
    <div className="animate-fade-in" style={{ maxWidth: 640, margin: '0 auto' }}>
      <div className="flex items-center gap-2 mb-1">
        <Radio size={20} style={{ color: 'var(--gold)' }} />
        <h1 className="section-title text-2xl">Set up {PRESS_BOX.NAME}</h1>
      </div>
      <div className="text-xs mb-4" style={{ color: 'var(--text-3)' }}>{PRESS_BOX.TAGLINE}</div>
      <StepHeader step={step} />

      {step === 1 && (
        <>
          <div className="card">
            <div className="text-sm mb-3" style={{ color: 'var(--text-2)' }}>
              Just the name and, if you have one, the logo — it shows at the top of the public stats page you&rsquo;ll share with parents.
              No player accounts, no payments: Press Box is only the games, the recordings and the stats.
            </div>
            <span className="label">Team name</span>
            <input className="input-field mb-3" maxLength={100} placeholder="Night Owls U10 BB"
              value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveTeamAndContinue()} autoFocus />
            <span className="label">Logo (optional)</span>
            <div className="flex items-center gap-3">
              {logoPreview
                ? <img src={logoPreview} alt="Team logo" style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover', border: '1px solid var(--border)' }} />
                : <div style={{ width: 56, height: 56, borderRadius: 12, border: '1px dashed var(--border)', display: 'grid', placeItems: 'center', color: 'var(--text-3)' }}><Radio size={20} /></div>}
              <button type="button" className="btn-ghost" onClick={() => logoRef.current?.click()}>
                <Upload size={14} /> {logoFile ? 'Change' : 'Choose an image'}
              </button>
              {logoFile && <button type="button" className="btn-ghost" style={{ padding: '10px 12px' }} onClick={() => { setLogoFile(null); setLogoPreview(null); }} aria-label="Remove logo"><X size={14} /></button>}
              <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={(e) => pickLogo(e.target.files?.[0])} />
            </div>
          </div>
          <StepNav onNext={saveTeamAndContinue} busy={creating}
            nextLabel={team ? 'Continue' : 'Create the team & continue'} />
        </>
      )}

      {step === 2 && team && (
        <>
          <ScheduleImportCard teamId={team.id} title="The season's games" onConfirmed={(d) => setGamesAdded((n) => n + (d.created || 0))} />
          {gamesAdded > 0 && (
            <div className="text-sm mb-1" style={{ color: 'var(--goal)' }}>
              <CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> {gamesAdded} game{gamesAdded === 1 ? '' : 's'} on the schedule.
            </div>
          )}
          <StepNav onBack={() => setStep(1)} onNext={() => setStep(3)}
            secondary={!gamesAdded ? (
              <button type="button" className="btn-ghost" onClick={() => setStep(3)}>Skip — I&rsquo;ll add games one by one</button>
            ) : null} />
        </>
      )}

      {step === 3 && team && (
        <>
          <div className="card">
            <div className="text-sm mb-3" style={{ color: 'var(--text-2)' }}>
              You&rsquo;re the team&rsquo;s manager and can already record, upload and change everything.
              If you&rsquo;re away for a game, invite another parent to record it: they get an email invitation,
              and what they can do is up to you — just record, record and fix stats, or full admin.
            </div>
            <div className="flex items-center justify-between gap-2 text-sm mb-3 px-3 py-2" style={{ background: 'var(--surface-2)', borderRadius: 10, flexWrap: 'wrap' }}>
              <span><strong>{user?.name || 'You'}</strong>{user?.email ? <span style={{ color: 'var(--text-3)' }}> — {user.email}</span> : null}</span>
              <span className="badge" style={{ background: 'var(--gold-a10, rgba(212,175,55,0.1))', color: 'var(--gold)', border: '1px solid var(--border)' }}>Manager · full admin</span>
            </div>
            <span className="label">Invite someone to record</span>
            <div className="flex items-center gap-2 mb-2" style={{ flexWrap: 'wrap' }}>
              <input className="input-field" style={{ flex: 1, minWidth: 170, padding: '10px 12px' }} type="email" placeholder="their@email.com"
                value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && invite()} />
              <input className="input-field" style={{ width: 150, padding: '10px 12px' }} placeholder="Name (optional)"
                value={inviteName} onChange={(e) => setInviteName(e.target.value)} />
            </div>
            <div className="mb-2">
              {ROLE_OPTIONS.map(([v, l, d]) => (
                <label key={v} className="flex items-start gap-2 text-sm mb-1" style={{ cursor: 'pointer' }}>
                  <input type="radio" name="pb-invite-role" style={{ marginTop: 3 }} checked={inviteRole === v} onChange={() => setInviteRole(v)} />
                  <span><strong>{l}</strong> <span style={{ color: 'var(--text-3)' }}>— {d}</span></span>
                </label>
              ))}
            </div>
            <button type="button" className="btn-ghost" onClick={invite} disabled={inviting}>
              {inviting ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send the invitation
            </button>
            {invited.map((e) => (
              <div key={e.email} className="text-sm mt-2" style={{ color: 'var(--text-2)' }}>
                <CheckCircle2 size={13} style={{ color: 'var(--goal)', verticalAlign: '-2px' }} /> {e.email} — invited ({roleLabel(e.role)})
              </div>
            ))}
            <div className="text-xs mt-3" style={{ color: 'var(--text-3)' }}>
              Next stop: add your players&rsquo; jersey numbers under <strong>Roster</strong> (snap a photo of the team list — the AI reads it),
              then turn on the public page in <strong>Site &amp; Settings</strong> and share the link with the parents.
            </div>
          </div>
          <StepNav onBack={() => setStep(2)} onNext={finish} nextLabel={`Done — open ${PRESS_BOX.NAME}`} nextIcon={<CheckCircle2 size={14} />} />
        </>
      )}
    </div>
  );
}
